import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FormLayout, Phase } from '../../../../models';
import { Row, Col, FormGroup, Label, Button, Card, CardBody } from 'reactstrap';
import { logOut, loadApplicationForm, createApplication } from '../../../../redux';
import { Loader, LoginForm, RegisterForm, FormRenderer, AvailabilitiesField } from '../../../../components';
import moment from 'moment';

import './ApplicationForm.css';

const ApplicationForm = props => {
    const dispatch              = useDispatch();
    const { t, i18n }           = useTranslation();
    const { tkt_instance_id }   = props;
    const { user }              = useSelector(state => state.auth);
    const { applicationTypeId } = useParams();

    const { form, formResources, isLoading }   = useSelector(state => state.applicationTypes);

    const [ error, setError ]  = useState(undefined);

    const { application, isLoadingOne, loadingErrorMessage } = useSelector(state => state.applications);

    useEffect(() => {
        if (applicationTypeId && tkt_instance_id) {
            dispatch(loadApplicationForm(
                applicationTypeId,
                tkt_instance_id
            ));
        }
    }, [tkt_instance_id]);

    useEffect(() => {
        moment.locale(i18n.language);
    }, [i18n.language]);

    const logout = () => {
        dispatch(logOut());
    }

    const submit = (values) => {
        const sectorsField = form?.fields?.find(f => f.type === FormLayout.SECTORS);
        if (sectorsField && !values[sectorsField.id]?.length)
            return setError(t('applications.please_choose_sector'));
        if (!values.availabilities?.length)
            return setError(t('applications.please_set_availabilities'));
        setError(undefined);

        return dispatch(createApplication({
            ...values,
            type_id: applicationTypeId,
        }, {
            tkt_instance: tkt_instance_id
        }));
    }

    const preFields  = React.useMemo(() => <></>, []);
    const postFields = React.useMemo(() => (
        <Col sm={12} md={12} >
            <hr />
            <Row>
                <Col sm={12} md={12} >
                    { error && (
                        <div className="bg-danger text-white rounded my-2 p-2">
                            { error }
                        </div>
                    )}
                    {formResources?.phases?.length > 0 && (
                        <FormGroup row>
                            <Col sm={12} className="text-left font-weight-bold">
                                <Label>
                                    { t('availabilities.phases_description') }
                                </Label>
                            </Col>
                            <Col sm={12}>
                                <dl className="text-center">
                                { formResources.phases.map(phase => new Phase(phase)).map(phase => (
                                    <Fragment key={phase.id}>
                                        <dt>{ phase.name.fr }</dt>
                                        <dd>
                                            {t('phases.from_to', {
                                                from: phase.start_at.format('LLL'),
                                                to: phase.stop_at.format('LLL')
                                            })}
                                        </dd>
                                    </Fragment>
                                ))}
                                </dl>
                            </Col>
                        </FormGroup>
                    )}
                    <FormGroup row>
                        <Col sm={12} className="text-left font-weight-bold">
                            <Label className="required">
                                { t('availabilities.availabilities_description') }
                            </Label>
                        </Col>
                        <Col sm={12}>
                            <AvailabilitiesField
                                preventSubmit
                                name="availabilities"
                                phases={ formResources?.phases }
                            />
                        </Col>
                    </FormGroup>
                </Col>
            </Row>
        </Col>
    ), [ error, formResources, i18n.language ]);

    // let's check if the current user has already submitted
    // an application of this type
    const hasAlreadySubmitted = React.useMemo(() => {
        return user?.applications?.reduce((has, application) => has || application.type_id === applicationTypeId, false);
    }, [user, applicationTypeId]);

    if (isLoading)
        return (
            <div className="loader-wrapper center light">
                <Loader color="#454545" />
            </div>
        );

    if (!form)
        return null;

    const email = formResources?.email?.mail[i18n.language];

    return (
        <div className="ApplicationForm">
            <div className="wrapper">
                <div className="container border rounded p-3 bg-white">
                    {!user?.id ? (
                    <>
                        <Row>
                            <Col sm={12} lg={6}>
                                <Card className="loginCard">
                                    <CardBody>
                                        <h3>{ t('login.title') }</h3>
                                        <LoginForm hide_logo email_helper={email} />
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col sm={12} lg={6}>
                                <Card className="registerCard">
                                    <CardBody>
                                        <h3>{ t('register.title') }</h3>
                                        <RegisterForm tkt_instance_id={ tkt_instance_id } hide_logo />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                    ) : (
                        <>
                            <div className="d-flex flex-column items-end">
                                <h6>
                                    <i class="fa fa-user"></i>&nbsp; { user.fullname }
                                </h6>
                                <Button onClick={logout} size="xs" color="warning">
                                    <i className="fa fa-sign-out" />&nbsp;
                                    { t('applications.logout') }
                                </Button>
                            </div>
                            {loadingErrorMessage && (
                                <div className="alert alert-danger">
                                    <span dangerouslySetInnerHTML={{__html: loadingErrorMessage}}></span>
                                </div>
                            )}
                            {application ? (
                                <div className="success-message">
                                    { t('applications.successfully_submitted') }
                                </div>
                            ) : (hasAlreadySubmitted ? (
                                <div className="already-message">
                                    <i className="fa fa-check" />&nbsp;
                                    { t('applications.already_submitted') }
                                </div>
                            ) : (
                                <FormRenderer
                                    formLayout={ form }
                                    onSubmit={ submit }
                                    isLoading={ isLoadingOne }
                                    preFields={ preFields }
                                    postFields={ postFields }
                                    resources={ formResources }
                                />
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ApplicationForm;
